// @flow
import React from 'react'
import I18n from 'i18n-react'

import VehicleInfoSubmodule from '../../submodules/vehicleInfo/src'

import { checkStateIfNeedToUpdate } from '../../Core/Components/_formFields/AbstractFormField'

import 'string_score'

// types
import type { DefaultMiniAppProps } from './'

type VehicleInfoAppProps = DefaultMiniAppProps & {
  miniAppData: {
    addGtmEvent: (action: string, label: string, value: string) => void,
    setCallbackTimer?: Function,
  },
}

type VehicleInfoAppState = {
  modalOpened: boolean,
  showAlert: boolean,
  setDataCl?: Function,
  setErrorsCl?: Function,
}

const config = {
  carInfoFields: [
    'manufacturer',
    'model',
    'version',
    'bodywork',
    'equipment',
    'capacity',
    'power',
    'category',
    'totalWeight',
    'firstTimeRegistration',
    'fuelType',
    'sittingPlaces',
    'vin',
    'technicalCardNo',
  ],
}

const fuelMap = {
  D: 'diesel',
  B: 'petrol',
}

export default class VehicleInfoApp extends React.Component<VehicleInfoAppProps, VehicleInfoAppState> {
  static defaultProps = {
    isBackOffice: false,
  }

  state = {
    modalOpened: false,
    showAlert: false,
  }

  shouldComponentUpdate(nextProps: VehicleInfoAppProps, nextState: VehicleInfoAppState) {
    return (
      JSON.stringify(this.props.activeSettings) !== JSON.stringify(nextProps.activeSettings) ||
      JSON.stringify(this.props.resources) !== JSON.stringify(nextProps.resources) ||
      JSON.stringify(this.props.formData) !== JSON.stringify(nextProps.formData) ||
      JSON.stringify(this.props.validationErrors) !== JSON.stringify(nextProps.validationErrors) ||
      checkStateIfNeedToUpdate(nextState, this.state)
    )
  }

  componentDidUpdate(prevProps: VehicleInfoAppProps) {
    const { formData, isBackOffice } = this.props
    const { setDataCl } = this.state
    if (typeof setDataCl === 'function') {
      const data = {}
      if (formData.getIn(['vin'], null) !== prevProps.formData.getIn(['vin'], null)) {
        data.modalVIN = formData.getIn(['vin'], null)
      }
      if (formData.getIn(['regPlate'], null) !== prevProps.formData.getIn(['regPlate'], null)) {
        data.modalRZ = formData.getIn(['regPlate'], null)
      }
      if (
        !isBackOffice &&
        formData.getIn(['policyHolder', 'phoneNumber'], null) !==
          prevProps.formData.getIn(['policyHolder', 'phoneNumber'], null)
      ) {
        data.modalPhone = formData.getIn(['policyHolder', 'phoneNumber'], null)
      }
      if (formData.getIn(['mileage'], null) !== prevProps.formData.getIn(['mileage'], null)) {
        data.modalMileage = formData.getIn(['mileage'], null)
      }
      if (formData.getIn(['verificationCode'], null) !== prevProps.formData.getIn(['verificationCode'], null)) {
        data.verificationCode = formData.getIn(['verificationCode'], null)
      }
      setDataCl(data)
    }
  }

  findAccurateValue = (resource: Array<string>, value: string) => {
    let result = { value: 'unknown', score: 0 }
    value = value.replace(/['`"´]/g, "'")
    if (resource) {
      for (let index = 0; index < resource.length; index++) {
        if (`${resource[index]}`.toLowerCase().replace(/['`"]/g, "'") === value) {
          return { value: resource[index], score: 1 }
        } else {
          const score = resource[index].replace(/['`"]/g, "'").score(value, 0.5)
          if (score >= 0.5 && score > result.score) {
            result = { value: resource[index], score }
          }
        }
      }
    }
    return result
  }

  handleCreate = (setDataCl: Function, setErrorsCl: Function) => {
    this.handleSetDefaultData(setDataCl)
    this.setState({ setErrorsCl })
  }

  handleSetDefaultData = (setDataCl: Function) => {
    if (typeof setDataCl === 'function') {
      this.setState({ setDataCl })
      const data = {}
      const vin = this.props.formData.getIn(['vin'], null)
      const rz = this.props.formData.getIn(['regPlate'], null)
      const phone = this.props.formData.getIn(['policyHolder', 'phoneNumber'], null)
      const verificationCode = this.props.formData.getIn(['verificationCode'], null)
      if (vin) {
        data['modalVIN'] = vin
      }
      if (rz) {
        data['modalRZ'] = rz
      }
      if (phone) {
        data['modalPhone'] = phone
      }
      if (verificationCode) {
        data['verificationCode'] = verificationCode
      }
      setDataCl(data)
    }
  }

  handleFinish = (finished: boolean, data: Object) => {
    const { onChangeFormData, resources, isBackOffice } = this.props
    let formData = this.props.formData
    let loadedData = {}
    if (finished) {
      this.setState({ modalOpened: false })
      const vehicleProperties = {}
      formData = formData.asMutable()
      const transformPaths = {
        manufacturer: 'manufacturer',
        model: 'model',
        capacity: 'capacity',
        power: 'power',
        category: 'category',
        totalWeight: 'weight',
        firstTimeRegistration: 'firstRegistered',
        fuelType: 'fuel',
        sittingPlaces: 'seats',
        vin: 'vin',
        modalRZ: 'regPlate',
        technicalCardNo: 'technicalCardNo',
      }
      const EnumRefInputs = {
        manufacturer: {
          resources: 'typeManufacturer',
          dependee: 'type',
          alternative: 'manufacturerByName',
        },
        model: {
          resources: 'manufacturerModel',
          dependee: 'manufacturer',
          alternative: 'modelByName',
        },
      }

      // $FlowIssue
      Object.entries(transformPaths).map((entry) => {
        const key = entry[1]
        let value = data[entry[0]]

        if (value) {
          if (EnumRefInputs[key]) {
            value = `${value}`.toLowerCase()
            const resource = resources[EnumRefInputs[key].resources][formData[EnumRefInputs[key].dependee]]
            const accurateItem = this.findAccurateValue(resource, value)
            if (accurateItem.value === 'unknown') {
              // formData[[EnumRefInputs[key].alternative]] = value
              formData[key] = ''
              loadedData[key] = 'notFound'
            } else {
              formData[key] = accurateItem.value
              loadedData[key] = accurateItem.value
            }
          } else if (key === 'fuel') {
            if (fuelMap[value]) {
              formData[key] = fuelMap[value]
              loadedData[key] = fuelMap[value]
              vehicleProperties[key] = fuelMap[value]
            }
          } else if (value || typeof value === 'boolean') {
            formData[key] = value
            loadedData[key] = value
            vehicleProperties[key] = value
          }
        } else {
          formData[key] = ''
          loadedData[key] = 'notFound'
        }
      })
      // yearOfConstruction
      if (formData.firstRegistered && formData.firstRegistered.includes('-')) {
        formData.yearOfConstruction = formData.firstRegistered.split('-')[0]
        loadedData.yearOfConstruction = formData.firstRegistered.split('-')[0]
      } else {
        formData.yearOfConstruction = ''
        loadedData.yearOfConstruction = 'notFound'
      }
      if (data.phoneNumber) {
        if (!formData.policyHolder) {
          formData.policyHolder = {
            phoneNumber: data.phoneNumber,
          }
        } else {
          formData.policyHolder = {
            ...formData.policyHolder,
            phoneNumber: data.phoneNumber,
          }
        }
      }

      this.setState({ showAlert: true })
      onChangeFormData(formData, loadedData)
      if (!this.props.isBackOffice) {
        this.props.miniAppData.addGtmEvent('Send', 'VIN')
      }
      if (this.props.miniAppData.setCallbackTimer && typeof this.props.miniAppData.setCallbackTimer === 'function') {
        this.props.miniAppData.setCallbackTimer()
      }
      if (this.props.miniAppData.setProperties && typeof this.props.miniAppData.setProperties === 'function') {
        this.props.miniAppData.setProperties({ vehicleProperties })
      }
    } else {
      const changed = []
      if (!isBackOffice && typeof data.modalPhone === 'string') {
        formData = formData.setIn(['policyHolder', 'phoneNumber'], data.modalPhone)
        changed.push('policyHolder', 'phoneNumber')
      }
      if (typeof data.modalRZ === 'string') {
        formData = formData.setIn(['regPlate'], data.modalRZ)
        changed.push('regPlate')
      }
      if (typeof data.modalMileage === 'string') {
        formData = formData.setIn(['mileage'], data.modalMileage)
        changed.push('mileage')
      }
      if (typeof data.modalVIN === 'string') {
        formData = formData.setIn(['vin'], data.modalVIN)
        changed.push('vin')
      }
      onChangeFormData(formData, loadedData)
    }
  }

  handleRenderBottomContent = () => (
    <span>
      {this.props.activeSettings &&
        this.props.activeSettings.domain &&
        I18n.translate('general.agreeWithTermsAndConditions', {
          link: (
            <a
              href={`${this.props.activeSettings.domain}informace-o-zpracovani-osobnich-udaju/`}
              rel="noopener noreferrer"
              target="_blank"
            >
              {I18n.translate('general.infoAboutDataProcessing')}
            </a>
          ),
        })}
    </span>
  )

  handleSetVerificationCode = (verificationCode: string) => {
    this.props.onChangeFormData({ verificationCode }, false)
  }

  render() {
    const { isBackOffice, formData, token, activeSettings, onReset, miniAppData } = this.props

    if (!['car', 'motorcycle', 'van', 'truck', 'campervan'].includes(formData.getIn(['type'], 'car'))) {
      return null
    }

    return (
      <VehicleInfoSubmodule
        activeSettings={activeSettings}
        config={config}
        isBackOffice={isBackOffice}
        key="vehicle-info-mini-app-miniApp"
        miniAppData={miniAppData}
        onCreate={this.handleCreate}
        onFinish={this.handleFinish}
        onReset={onReset}
        onSetVerificationCode={this.handleSetVerificationCode}
        token={token}
      />
    )
  }
}
